<template>
  <v-dialog :value="value" max-width="640" persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="text-h6">{{ $t('dueDiligence.thirdPartyExport.title') }}</v-card-title>

      <!-- Step 1: choose the third-party system -->
      <v-card-text v-if="step === 1">
        <p class="text-body-2">{{ $t('dueDiligence.thirdPartyExport.selectIntro') }}</p>
        <v-radio-group v-model="selectedKey" class="mt-1" hide-details>
          <v-radio v-for="p in providers" :key="p.key" :label="p.label" :value="p.key" />
        </v-radio-group>
      </v-card-text>

      <!-- Step 2: transformations to authorize + missing info, for the chosen system -->
      <v-card-text v-else-if="step === 2 && provider">
        <p class="text-body-2">{{ $t(prefix + '.intro') }}</p>

        <template v-if="applicableCategories.length">
          <p class="text-body-2 font-weight-medium mb-1">{{ $t('dueDiligence.thirdPartyExport.transformsIntro') }}</p>
          <v-card v-for="cat in applicableCategories" :key="cat" outlined class="mb-2 pa-3">
            <div class="font-weight-medium">{{ $t(prefix + '.cat.' + cat + '.label') }}</div>
            <div class="text-body-2 grey--text text--darken-1">{{ $t(prefix + '.cat.' + cat + '.desc') }}</div>
            <div class="text-caption mt-1">
              {{ $t('dueDiligence.thirdPartyExport.exampleLabel') }}:
              <code>{{ $t(prefix + '.cat.' + cat + '.example') }}</code>
            </div>
            <v-checkbox
              v-model="accepted[cat]"
              dense
              hide-details
              class="mt-2"
              :label="$t('dueDiligence.thirdPartyExport.authorizeEach')" />
          </v-card>
        </template>
        <p v-else class="text-body-2">{{ $t('dueDiligence.thirdPartyExport.noTransforms') }}</p>

        <template v-if="resolvableMissing.length">
          <v-divider class="my-3" />
          <p class="text-body-2 font-weight-medium mb-1">{{ $t('dueDiligence.thirdPartyExport.missingTitle') }}</p>
          <p class="text-body-2 grey--text text--darken-1">
            {{ $t('dueDiligence.thirdPartyExport.missingIntro', { system: provider.label }) }}
          </p>
          <div v-for="m in resolvableMissing" :key="m.index + '-' + m.field" class="mb-2">
            <div class="text-caption">{{ $t('dueDiligence.thirdPartyExport.plotLabel') }}: {{ m.plot || ('#' + (m.index + 1)) }}</div>
            <v-select
              v-if="fieldInput(m.field).type === 'select'"
              v-model="resolutions[m.index][fieldInput(m.field).resolutionKey]"
              :items="fieldInput(m.field).items"
              dense outlined hide-details
              :label="$t(prefix + '.field.' + m.field)" />
            <v-text-field
              v-else
              v-model="resolutions[m.index][fieldInput(m.field).resolutionKey]"
              dense outlined hide-details
              :maxlength="fieldInput(m.field).maxlength"
              :label="$t(prefix + '.field.' + m.field)" />
          </div>
        </template>

        <v-alert v-if="blockingMissing.length" type="error" dense text class="mt-3 mb-0">
          <div v-for="m in blockingMissing" :key="m.index + '-' + m.field" class="text-body-2">
            {{ (m.plot || ('#' + (m.index + 1))) }} — {{ $t(prefix + '.field.' + m.field) }}
          </div>
        </v-alert>

        <v-divider class="my-3" />
        <div class="text-body-2 font-weight-medium">{{ $t('dueDiligence.thirdPartyExport.importantTitle') }}</div>
        <ul class="text-caption grey--text text--darken-1 mt-1">
          <li v-for="dk in provider.disclaimerKeys" :key="dk">{{ $t(prefix + '.' + dk) }}</li>
        </ul>
      </v-card-text>

      <v-card-actions>
        <v-btn v-if="step === 2" text @click="step = 1">{{ $t('dueDiligence.thirdPartyExport.back') }}</v-btn>
        <v-spacer />
        <v-btn text @click="close">{{ $t('dueDiligence.thirdPartyExport.cancel') }}</v-btn>
        <v-btn v-if="step === 1" color="primary" :disabled="!selectedKey" @click="goReview">
          {{ $t('dueDiligence.thirdPartyExport.next') }}
        </v-btn>
        <v-btn v-else color="primary" :disabled="!canGenerate" @click="onGenerate">
          {{ $t('dueDiligence.thirdPartyExport.generate') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { listProviders, getProvider } from '@/utils/thirdPartyExport';

export default {
  name: 'ThirdPartyExportDialog',
  props: {
    value: { type: Boolean, default: false },
    productionPlaces: { type: Array, default: () => [] },
    report: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      step: 1,
      selectedKey: '',
      accepted: {},
      resolutions: {},
    };
  },
  computed: {
    providers() {
      return listProviders();
    },
    provider() {
      return getProvider(this.selectedKey);
    },
    prefix() {
      return this.provider ? this.provider.i18nPrefix : '';
    },
    validation() {
      if (!this.provider) return { categories: [], missing: [], duplicates: [] };
      return this.provider.validate(this.productionPlaces, this.report);
    },
    applicableCategories() {
      return this.validation.categories;
    },
    resolvableMissing() {
      const fi = (this.provider && this.provider.fieldInput) || {};
      return this.validation.missing.filter((m) => !!fi[m.field]);
    },
    blockingMissing() {
      const fi = (this.provider && this.provider.fieldInput) || {};
      return this.validation.missing.filter((m) => !fi[m.field]);
    },
    canGenerate() {
      if (!this.provider || this.blockingMissing.length) return false;
      const allAccepted = this.applicableCategories.every((c) => this.accepted[c]);
      const allResolved = this.resolvableMissing.every((m) => {
        const key = this.fieldInput(m.field).resolutionKey;
        const r = this.resolutions[m.index] || {};
        const v = r[key];
        return typeof v === 'string' ? !!v.trim() : !!v;
      });
      return allAccepted && allResolved;
    },
  },
  watch: {
    value(open) {
      if (open) this.resetAll();
    },
  },
  methods: {
    fieldInput(field) {
      return ((this.provider && this.provider.fieldInput) || {})[field] || {};
    },
    resetAll() {
      this.step = 1;
      // Keep a single registered provider pre-selected for convenience; the user still confirms.
      this.selectedKey = this.providers.length === 1 ? this.providers[0].key : '';
      this.accepted = {};
      this.resolutions = {};
    },
    goReview() {
      if (!this.provider) return;
      const accepted = {};
      this.applicableCategories.forEach((c) => { accepted[c] = false; });
      this.accepted = accepted;
      const resolutions = {};
      this.resolvableMissing.forEach((m) => {
        if (!resolutions[m.index]) resolutions[m.index] = {};
      });
      this.resolutions = resolutions;
      this.step = 2;
    },
    close() {
      this.$emit('input', false);
    },
    onGenerate() {
      if (!this.canGenerate) return;
      const acceptedCategories = this.applicableCategories.filter((c) => this.accepted[c]);
      this.$emit('generate', {
        providerKey: this.selectedKey,
        acceptedCategories,
        resolutions: this.resolutions,
      });
      this.close();
    },
  },
};
</script>
